// Login passwordless por correo — código OTP de 6 dígitos.
//
// Por qué código y no enlace clickeable: el enlace en móvil rebota
// (correo -> navegador -> deep link) y se rompe en varios equipos. El código
// de 6 dígitos se teclea idéntico en web y en la app nativa (llamada directa a
// Supabase, sin deep links ni rebotes), así que "web + Android" cuesta casi lo
// mismo que solo web. Y sobre todo elimina la fricción #1 del login solo-Google:
// la gente olvida su clave de Google y no entra. Passwordless = no hay clave
// que olvidar.
//
// Convive con signInWithGoogle (lib/nativeAuth.js). NO toca el gate de edad ni
// el onboarding: esos corren después del login, igual para cualquier proveedor.
import { supabase } from './supabase';

// Formato mínimo. No reemplaza la verificación real (el código solo llega si el
// correo existe), pero evita pegarle a Supabase con basura y da error inmediato.
const RE_EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function emailValido(email) {
  return typeof email === 'string' && RE_EMAIL.test(email.trim());
}

// Normaliza el correo para que "Juan@Mail.cl " y "juan@mail.cl" sean la misma
// cuenta (Supabase distingue por el string exacto al enviar/verificar).
function normalizar(email) {
  return (email || '').trim().toLowerCase();
}

// Envía el código de 6 dígitos al correo. shouldCreateUser: true unifica
// registro y login: si el correo es nuevo, se crea la cuenta en el mismo flujo.
// Devuelve { error } con mensaje en español listo para mostrar.
export async function enviarCodigoEmail(email) {
  const correo = normalizar(email);
  if (!emailValido(correo)) {
    return { data: null, error: { message: 'Escribe un correo válido.' } };
  }
  return supabase.auth.signInWithOtp({
    email: correo,
    options: { shouldCreateUser: true },
  });
}

// Supabase envía OTP de 6 a 8 dígitos según la config del proyecto; aceptamos el
// rango en vez de fijar 6, así el largo del código no rompe el login.
const RE_CODIGO = /^\d{6,8}$/;

// ¿Lo tecleado es un código de un solo uso, o una contraseña?
//
// POR QUÉ EXISTE ESTA PREGUNTA (2026-09-09). MiPAE no tiene contraseñas: se
// entra con Google o con un código que llega al correo. Eso deja afuera a quien
// no puede abrir ese buzón, y el caso concreto es el revisor de App Store: Apple
// exige credenciales para probar la app y su equipo no recibe nuestro correo.
// En vez de abrir una segunda puerta en la pantalla, el MISMO campo acepta las
// dos cosas y el largo decide cuál es. Para quien usa MiPAE no cambia nada:
// nadie tiene contraseña, así que nunca cae en esta rama.
//
// La contraseña es NUMÉRICA a propósito: el campo declara inputMode="numeric",
// que en el teléfono abre un teclado sin letras. Una contraseña con letras sería
// imposible de escribir justo en el aparato donde se prueba la app.
export function esCodigoOtp(secreto) {
  return RE_CODIGO.test((secreto || '').trim());
}

// Más larga que cualquier código que Supabase pueda mandar, para que las dos
// ramas no se pisen nunca.
const RE_CLAVE = /^\d{9,}$/;

// Verifica lo tecleado. type: 'email' es el OTP de correo (no el del magic link
// clickeable). Si valida, Supabase deja la sesión y el listener de
// onAuthStateChange en App.jsx dispara el resto (perfil, gate de edad, home).
export async function verificarCodigoEmail(email, token) {
  const correo = normalizar(email);
  const codigo = (token || '').trim();
  if (!emailValido(correo)) {
    return { data: null, error: { message: 'Escribe un correo válido.' } };
  }
  if (esCodigoOtp(codigo)) {
    return supabase.auth.verifyOtp({ email: correo, token: codigo, type: 'email' });
  }
  // Solo lo MÁS LARGO que cualquier código posible es una contraseña. Un "12345"
  // es alguien que se equivocó tecleando su código, no una clave, y tiene que
  // seguir recibiendo el mensaje de siempre.
  if (RE_CLAVE.test(codigo)) {
    return supabase.auth.signInWithPassword({ email: correo, password: codigo });
  }
  return { data: null, error: { message: 'Revisa el código: son solo números.' } };
}

// Entrar con correo y contraseña, en un formulario propio (2026-10-05, quinto
// rechazo de la 1.3.0). La contraseña escondida en el campo del código no le
// sirvió al revisor: pidió el código para la cuenta de prueba (auth_logs: el
// /otp desde una IP de Apple) y nunca volvió a escribir nada, porque la
// pantalla le decía "Te enviamos un código" a un buzón que no puede abrir. Un
// revisor busca un campo de contraseña de verdad, así que ahora existe. Solo
// se ofrece en la app de iOS (ver Home.jsx); en MiPAE nadie más tiene clave.
// Acepta cualquier carácter: este campo no filtra a dígitos como el del código.
export async function entrarConClave(email, clave) {
  const correo = normalizar(email);
  if (!emailValido(correo)) {
    return { data: null, error: { message: 'Escribe un correo válido.' } };
  }
  if (!clave) {
    return { data: null, error: { message: 'Escribe tu contraseña.' } };
  }
  const { data, error } = await supabase.auth.signInWithPassword({ email: correo, password: clave });
  if (error) {
    return { data, error: { message: 'Correo o contraseña incorrectos.' } };
  }
  return { data, error: null };
}
